// ─────────────────────────────────────────────────────────────────────────────
// Note Type Studio — schema helpers
//
// The builder works on a rich model:
//   { name, desc, sections:[{ id, title, icon, carePlan?, cond, fields:[field] }],
//     settings }
// where field = { id, type, label, placeholder, help, span, required, options[],
//                 conditional, calc, _src? }
//
// The backend NType stores a flat `custom[]` (runtime source of truth) plus an
// additive `sections[]` (this rich schema, for round-tripping) and
// `templateSettings`. These helpers convert between the two so:
//   • existing note types (no `sections`) open in the builder by deriving one
//     section from their `custom[]`, and
//   • saving never drops data — each derived field keeps its original custom
//     entry (`_src`) so the persisted `custom[]` preserves fieldType ids,
//     choice arrays, priority/viewOnly, etc.
// ─────────────────────────────────────────────────────────────────────────────

export const TYPE_META = {
  text: { ic: "text", label: "Text Field" },
  textarea: { ic: "para", label: "Text Area" },
  number: { ic: "num", label: "Number" },
  date: { ic: "date", label: "Date" },
  time: { ic: "time", label: "Time" },
  dropdown: { ic: "drop", label: "Dropdown" },
  single: { ic: "single", label: "Single Choice" },
  multi: { ic: "multi", label: "Multiple Choice" },
  toggle: { ic: "toggle", label: "Yes / No" },
  chips: { ic: "chips", label: "Severity" },
  client: { ic: "client", label: "Client(s)" },
  staff: { ic: "staff", label: "Staff" },
  facility: { ic: "facility", label: "Facility" },
  notifications: { ic: "notif", label: "Notifications" },
  calc: { ic: "calc", label: "Calculation" },
  careplan: { ic: "careplan", label: "Care Plan" },
};

export const PALETTE = [
  { group: "Layout", cls: "layout", items: [{ type: "section", ic: "rows", name: "Section" }] },
  {
    group: "Basic",
    cls: "",
    items: [
      { type: "text", ic: "text", name: "Text" },
      { type: "textarea", ic: "para", name: "Text Area" },
      { type: "number", ic: "num", name: "Number" },
      { type: "date", ic: "date", name: "Date" },
      { type: "time", ic: "time", name: "Time" },
    ],
  },
  {
    group: "Choice",
    cls: "",
    items: [
      { type: "dropdown", ic: "drop", name: "Dropdown" },
      { type: "single", ic: "single", name: "Single Choice" },
      { type: "multi", ic: "multi", name: "Multiple Choice" },
      { type: "toggle", ic: "toggle", name: "Yes / No" },
      { type: "chips", ic: "chips", name: "Severity Chips" },
    ],
  },
  {
    group: "Data",
    cls: "data",
    items: [
      { type: "client", ic: "client", name: "Client(s)" },
      { type: "staff", ic: "staff", name: "Staff" },
      { type: "facility", ic: "facility", name: "Facility" },
      { type: "notifications", ic: "notif", name: "Notifications" },
    ],
  },
  {
    group: "Advanced",
    cls: "adv",
    items: [
      { type: "calc", ic: "calc", name: "Calculation" },
      { type: "careplan", ic: "careplan", name: "Care Plan" },
    ],
  },
];

export const SECTION_ICONS = ["tag", "pin", "users", "doc", "cross", "shield", "notif", "check", "calc"];
export const HAS_OPTS = (t) => ["dropdown", "single", "multi", "chips"].includes(t);

// ── care plan parts ───────────────────────────────────────────────────────────
// A `careplan` field asks "Use care plan?" on the note. Which parts of the
// client's care plan it then opens for documentation is up to the template
// author — the Support Plan Outcomes block, the Essential Support block, or
// BOTH. Each block only appears if the client has that plan type active.
export const CARE_PLAN_PARTS = [
  {
    key: "outcomes",
    short: "Outcomes",
    label: "Support plan outcomes",
    hint: "The note documents against the client's active Support Plan outcomes.",
  },
  {
    key: "essential",
    short: "Essential",
    label: "Essential supports",
    hint: "The note documents against the client's active Essential Supports plan.",
  },
];
const CARE_PLAN_PART_KEYS = CARE_PLAN_PARTS.map((p) => p.key);

// Stored as an array (`carePlanParts`) so the two blocks stay individually
// addressable and can be selected together. Normalising here is what guarantees
// every read site gets a clean, de-duplicated, canonically ordered list holding
// AT LEAST one part: a template authored before this was configurable has no
// `carePlanParts` and only ever surfaced Support Plan Outcomes, so absent — or
// entirely unrecognised — resolves to outcomes as the default.
export const carePlanPartsOf = (field) => {
  if (!field || field.type !== "careplan") return [];
  const stored = Array.isArray(field.carePlanParts) ? field.carePlanParts : [];
  const parts = CARE_PLAN_PART_KEYS.filter((k) => stored.includes(k));
  return parts.length ? parts : ["outcomes"];
};

// Client/staff/facility fields are looked up all over the app (reports, PDFs,
// billing, mobile chart, draft notes) by these exact hardcoded keys — e.g.
// `fields["Client Name"]`. Unlike general fields, their name can't be
// arbitrary user text or every one of those consumers silently breaks.
export const LOCKED_LABEL = { client: "Client Name", staff: "Staff Name", facility: "Facility Name" };
// The legacy builder always stamped these onto a client/staff/facility field's
// `options` string so list/table/report consumers (getOptions(), NoteTableRow)
// know which live record list to resolve the stored id against. HAS_OPTS
// deliberately excludes these types (they don't have user-defined choices), so
// customFromSections has to stamp this marker itself instead of relying on
// the normal "has options" branch general fields go through.
export const LOCKED_OPTIONS = { client: "Clients", staff: "Staff", facility: "Facilities" };

let _uid = 0;
export const nid = () => "f" + Date.now().toString(36) + "_" + ++_uid;

// ── storage keys ──────────────────────────────────────────────────────────────
// A field's answer is stored under a single flat key (`custom[].field`, and the
// same key in a note's `fields` map — see StudioSectionsForm). Field NAMES only
// have to be unique inside their own section, so two sections can both have a
// "Beginning Balance"; the storage key can't collide though, or both controls
// would bind to the same value and only one answer would survive a save.
//
// So: the first field to claim a name keeps it verbatim (existing templates and
// their saved notes are untouched), and any later field with the same name is
// stored under "<name> (<section title>)", falling back to a numeric suffix.
// A key already persisted on the field wins, so keys stay put across edits.
export const fieldStorageKeys = (model) => {
  const keys = {};
  const taken = new Set();
  const claim = (k) => taken.add(k.trim().toLowerCase());
  const isTaken = (k) => taken.has(k.trim().toLowerCase());

  const fields = (model?.sections || []).flatMap((s) =>
    (s.fields || []).map((f) => ({ f, s }))
  );

  const labelCounts = {};
  fields.forEach(({ f }) => {
    const l = (f.label || "").trim().toLowerCase();
    if (l) labelCounts[l] = (labelCounts[l] || 0) + 1;
  });

  // Pass 1 — reserve the keys that are already fixed: locked client/staff/
  // facility names, and a persisted key while the label it stands in for is
  // still ambiguous. Once the clash is gone the key follows the label again,
  // so `custom[].field` (what tables and reports label the column with) doesn't
  // keep a stale "(Section)" suffix forever.
  //
  // Only the FIRST client/staff/facility field claims the locked key. That one
  // stays the note's canonical client/staff/facility — the backend's note-list
  // visibility filter, the tables, reports, PDFs and billing all read
  // `fields["Client Name"]` and must keep finding it. A template may hold more
  // than one, though, and the extras are genuinely independent questions: they
  // fall through to the label-derived keys below and hold their own answer.
  // 1a — an EXTRA client/staff/facility field always carries a pinned key
  // (sectionsForStore writes one for any of them that isn't the locked key), and
  // it keeps that key rather than competing for the locked slot. Without this,
  // "first" is purely positional and reordering the sections would hand
  // "Client Name" to the other field, orphaning both fields' saved answers.
  fields.forEach(({ f }) => {
    const locked = LOCKED_LABEL[f.type];
    const persisted = (f.key || "").trim();
    if (locked && persisted && persisted !== locked) {
      keys[f.id] = persisted;
      claim(persisted);
    }
  });
  // 1b — the locked key goes to the first field of that type left over.
  const lockedClaimed = new Set();
  fields.forEach(({ f }) => {
    const locked = LOCKED_LABEL[f.type];
    if (!locked || keys[f.id] || lockedClaimed.has(f.type)) return;
    lockedClaimed.add(f.type);
    keys[f.id] = locked;
    claim(locked);
  });
  // 1c — an ordinary field's persisted key, while the label it stands in for is
  // still ambiguous.
  fields.forEach(({ f }) => {
    if (keys[f.id]) return;
    const persisted = (f.key || "").trim();
    const label = (f.label || "").trim();
    if (persisted && label && labelCounts[label.toLowerCase()] > 1) {
      keys[f.id] = persisted;
      claim(persisted);
    }
  });

  // Pass 2 — everything else derives from its label.
  fields.forEach(({ f, s }) => {
    if (keys[f.id]) return;
    const base = (f.label || "").trim();
    if (!base) {
      keys[f.id] = base;
      return;
    }
    let key = base;
    if (isTaken(key)) {
      const title = (s.title || "").trim();
      const scoped = title ? `${base} (${title})` : base;
      key = scoped;
      let n = 2;
      while (isTaken(key)) key = `${scoped} ${n++}`;
    }
    claim(key);
    keys[f.id] = key;
  });

  return keys;
};

// Best-effort map of a legacy custom field's `field_type` string to a builder type.
// Only used as a last-resort fallback (see typeFromFieldType below) — this
// string is usually just the constant marker "Custom Field"/"Field" stamped
// by the legacy builder, not a real type name.
const guessType = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (s.includes("textarea") || s.includes("text area") || s.includes("description")) return "textarea";
  if (s.includes("date")) return "date";
  if (s.includes("time")) return "time";
  if (s.includes("number") || s.includes("numeric")) return "number";
  if (s.includes("dropdown")) return "dropdown";
  if (s.includes("multi")) return "multi";
  if (s.includes("single")) return "single";
  if (s.includes("severity") || s.includes("chips")) return "chips";
  if (s.includes("client")) return "client";
  if (s.includes("staff")) return "staff";
  if (s.includes("facilit")) return "facility";
  return "text";
};


const TYPE_FROM_FIELD_TYPE_NAME = {
  text: "text",
  string: "text",
  "text area": "textarea",
  textarea: "textarea",
  number: "number",
  numeric: "number",
  date: "date",
  time: "time",
  dropdown: "dropdown",
  "single choice": "single",
  "multiple choice": "multi",
  toggle: "toggle",
  "yes/no": "toggle",
  severity: "chips",
  chips: "chips",
  client: "client",
  clients: "client",
  staff: "staff",
  facility: "facility",
  facilities: "facility",
  notifications: "notifications",
  calculation: "calc",
  "care plan": "careplan",
};

const typeFromFieldType = (fieldType) => TYPE_FROM_FIELD_TYPE_NAME[String(fieldType?.name || "").trim().toLowerCase()];

const optionsFromCustom = (c) => {
  if (Array.isArray(c?.dropdownChoice) && c.dropdownChoice.length) return [...c.dropdownChoice];
  if (Array.isArray(c?.singleChoice) && c.singleChoice.length) return [...c.singleChoice];
  if (Array.isArray(c?.multipleChoice) && c.multipleChoice.length) return [...c.multipleChoice];
  return [];
};

// Build the editable model from an existing note type (or a blank one).
export const modelFromNoteType = (note) => {
  const settings = {
    approvers: [],
    allApprovers: true,
    // Availability: allStaff true = everyone except `staffExcluded`;
    // false = only `staff`. Both hold staff ids.
    allStaff: true,
    staff: [],
    staffExcluded: [],
    ...(note?.templateSettings || {}),
    // approvalRequired is owned by the top-level `approvalRequired` field (the
    // runtime's source of truth). It wins over any legacy copy that older
    // templates stored inside templateSettings, so the toggle reflects reality.
    approvalRequired: !!note?.approvalRequired,
  };

  // Already builder-authored → reuse its sections (ensuring ids/shape).
  if (Array.isArray(note?.sections) && note.sections.length) {
    // `note` is often a reference into Redux state, which Redux Toolkit/Immer
    // freezes in development to catch accidental mutation. The .map() below
    // makes a fresh object per field, but nested objects (calc/options/
    // conditional) were being assigned BY REFERENCE — so the wrapper field
    // object was mutable but its nested `calc` object stayed frozen,
    // silently breaking the Studio's direct-mutation edit pattern (e.g. the
    // Calculation panel's "Insert field" picker writing to f.calc.formula)
    // specifically for fields loaded from a saved note type, while a calc
    // field freshly added in the same session (never touching Redux) worked
    // fine. Deep-cloning each nested object fixes this.
    const cloneJSON = (v) => (v ? JSON.parse(JSON.stringify(v)) : v);
    return {
      id: note?.id || null,
      name: note?.name || "",
      desc: note?.description || "",
      general: !!note?.general,
      sections: note.sections.map((s) => ({
        id: s.id || "s" + nid(),
        title: s.title || "Section",
        icon: s.icon || "tag",
        carePlan: !!s.carePlan,
        cond: cloneJSON(s.cond) || null,
        fields: (s.fields || []).map((f) => ({
          id: f.id || nid(),
          type: f.type || "text",
          // The author's own label wins, including for client/staff/facility:
          // the storage key no longer follows their label (fieldStorageKeys
          // assigns it by position), so forcing "Client Name" back on here only
          // stopped two client fields from being told apart on the note.
          label: f.label || LOCKED_LABEL[f.type] || "",
          // The key this field's answers are already stored under (only present
          // when it had to differ from the label — see fieldStorageKeys).
          key: f.key || "",
          placeholder: f.placeholder || "",
          help: f.help || "",
          span: f.span || 12,
          required: !!f.required,
          options: f.options ? [...f.options] : [],
          // Normalised on the way in so the inspector's checkboxes always have a
          // concrete list to render, including for pre-carePlanParts templates.
          ...(f.type === "careplan" ? { carePlanParts: carePlanPartsOf(f) } : {}),
          conditional: cloneJSON(f.conditional) || null,
          calc: cloneJSON(f.calc) || null,
          _src: cloneJSON(f._src),
        })),
      })),
      settings,
    };
  }

  // Legacy type → one section derived from custom[], preserving originals in _src.
  const fields = (note?.custom || []).map((c, i) => {
    const type = typeFromFieldType(c?.fieldType) || guessType(c?.field_type);
    return {
      id: nid() + "_" + i,
      type,
      label: LOCKED_LABEL[type] || c?.field || "",
      // A legacy entry's `field` IS its storage key — pin it so reopening and
      // re-saving the template can never move existing notes' answers.
      key: c?.field || "",
      placeholder: "",
      help: "",
      span: type === "textarea" || HAS_OPTS(type) ? 12 : 6,
      required: !!c?.priority,
      options: optionsFromCustom(c),
      conditional: null,
      calc: null,
      _src: c, // keep the exact original entry so save preserves fieldType id etc.
    };
  });

  return {
    id: note?.id || null,
    name: note?.name || "",
    desc: note?.description || "",
    general: !!note?.general,
    sections: [{ id: "s" + nid(), title: "General", icon: "tag", cond: null, fields }],
    settings,
  };
};

export const emptyModel = () => ({
  id: null,
  name: "",
  desc: "",
  general: false,
  sections: [{ id: "s" + nid(), title: "General", icon: "tag", cond: null, fields: [] }],
  settings: {
    approvalRequired: false,
    approvers: [],
    allApprovers: true,
    allStaff: true,
    staff: [],
    staffExcluded: [],
  },
});

const allFields = (model) => model.sections.flatMap((s) => s.fields);

// A legacy custom entry's `fieldType` is a raw ObjectId in the DB, but the GET
// /ntype response populates it into a full object ({ name, createdAt, id, ... }).
// Sending that populated object back on save makes Mongoose fail to cast it to an
// ObjectId ("Cast to ObjectId failed for value {...}"). Collapse it back to the
// bare id string so the round-trip preserves the reference the backend expects.
const normalizeFieldType = (ft) => {
  if (ft && typeof ft === "object") return ft.id || ft._id || ft.$oid || ft;
  return ft;
};

// Flatten sections → custom[] for the backend runtime, preserving legacy data.
// `field` carries the STORAGE key, which is the label for all but same-named
// fields in different sections (see fieldStorageKeys).
export const customFromSections = (model, keys = fieldStorageKeys(model)) =>
  allFields(model).map((f) => {
    const storageKey = keys[f.id] || f.label;
    if (f._src) {
      // Retained legacy field — keep everything, just track the (possibly new)
      // label. Self-heal a blank `options: ""` left over from an older,
      // buggy save (see the comment below) by dropping the key entirely
      // instead of re-persisting an empty string.
      const src = { ...f._src };
      if (LOCKED_OPTIONS[f.type]) src.options = LOCKED_OPTIONS[f.type];
      else if (src.options === "") delete src.options;
      if ("fieldType" in src) src.fieldType = normalizeFieldType(src.fieldType);
      return {
        ...src,
        field: storageKey,
        field_type: f._src.field_type || f.type,
      };
    }
    const entry = {
      field: storageKey,
      field_type: f.type,
      priority: !!f.required,
      viewOnly: false,
      others: "",
    };
    // Only stamp `options` when it's actually meaningful (the client/staff/
    // facility marker consumers key off). Leaving it unset for every other
    // type — rather than an empty string — matters: `"options" in entry` is
    // how table/report consumers (e.g. NoteTableRow.getFields()) decide
    // whether a field's saved value needs resolving against a live options
    // list. An empty-string `options` still satisfies that `in` check, so a
    // plain field like Date/Time got misrouted into the lookup branch and
    // always resolved to `undefined` instead of its real saved value.
    if (LOCKED_OPTIONS[f.type]) entry.options = LOCKED_OPTIONS[f.type];
    if (HAS_OPTS(f.type)) {
      if (f.type === "dropdown") entry.dropdownChoice = f.options || [];
      else if (f.type === "single") entry.singleChoice = f.options || [];
      else entry.multipleChoice = f.options || [];
    }
    return entry;
  });

// Clean sections for storage (drop the transient _src pointer).
const sectionsForStore = (model, keys = fieldStorageKeys(model)) =>
  model.sections.map((s) => ({
    id: s.id,
    title: s.title,
    icon: s.icon,
    ...(s.carePlan ? { carePlan: true } : {}),
    ...(s.cond ? { cond: s.cond } : {}),
    fields: s.fields.map((f) => {
      const o = { id: f.id, type: f.type, label: f.label, span: f.span, required: !!f.required };
      // Only persisted when it had to differ from the label, so existing
      // templates round-trip byte-for-byte. A client/staff/facility field is
      // the exception: its key is pinned whenever it ISN'T the locked one, even
      // where it happens to equal the label, because "which one is first" is
      // positional and a section reorder would otherwise move the locked key
      // onto the other field (see fieldStorageKeys).
      const storageKey = keys[f.id];
      const locked = LOCKED_LABEL[f.type];
      const pin = locked ? storageKey !== locked : storageKey !== f.label;
      if (storageKey && pin) o.key = storageKey;
      if (f.placeholder) o.placeholder = f.placeholder;
      if (f.help) o.help = f.help;
      if (HAS_OPTS(f.type)) o.options = f.options || [];
      if (f.type === "careplan") o.carePlanParts = carePlanPartsOf(f);
      if (f.type === "calc" && f.calc) o.calc = f.calc;
      if (f.conditional) o.conditional = f.conditional;
      return o;
    }),
  }));

// The payload sent to POST/PUT /ntype.
export const noteTypePayload = (model) => {
  // approvalRequired lives ONLY on the top-level field (the runtime's source of
  // truth). Strip it out of templateSettings so there's a single source and the
  // two can never drift apart. aiDrafting / voiceToText are dropped entirely —
  // the "Documentation" settings section was removed.
  const {
    approvalRequired: _approvalRequired,
    aiDrafting: _aiDrafting,
    voiceToText: _voiceToText,
    ...templateSettings
  } = model.settings || {};
  // One resolution for both shapes, so `sections[].key` and `custom[].field`
  // can never disagree about where a field's answer lives.
  const keys = fieldStorageKeys(model);
  const payload = {
    name: (model.name || "").trim(),
    description: (model.desc || "").trim(),
    sections: sectionsForStore(model, keys),
    custom: customFromSections(model, keys),
    templateSettings,
    approvalRequired: !!model.settings?.approvalRequired,
  };
  if (model.id) payload.id = model.id;
  return payload;
};
